import { Injectable, inject, signal } from '@angular/core';
import { BetaFeature, USER_SETTINGS_SERVICE } from '@infinitform/platform-core';

/**
 * Gates experimental GUI features. A feature is shown only when the user
 * opted in via the Beta Features section of the cabinet settings page
 * (persisted in UserSettings.betaFeatures).
 */
@Injectable({
  providedIn: 'root'
})
export class BetaFeaturesService {
  private readonly userSettingsService = inject(USER_SETTINGS_SERVICE, { optional: true });

  private readonly enabledKeys = signal<string[]>([]);

  /** Whether a load has already completed, so a refresh is a genuine re-read. */
  private loaded = false;

  /**
   * Read the opt-ins. Called on entering the editor, which every consumer of
   * this service sits inside -- so there is no constructor fetch to duplicate
   * it.
   *
   * The first load joins whatever shared settings read is already in flight.
   * Only a later call drops that shared response, which is what makes desktop
   * pick up toggles changed in the cabinet without a reload.
   */
  refresh(): void {
    if (!this.userSettingsService) {
      return;
    }
    if (this.loaded) {
      this.userSettingsService.invalidate();
    }
    this.userSettingsService.get().subscribe({
      next: (settings) => {
        this.enabledKeys.set(settings?.betaFeatures ?? []);
        this.loaded = true;
      },
      error: () => this.enabledKeys.set([])
    });
  }

  isEnabled(key: BetaFeature): boolean {
    return this.enabledKeys().includes(key);
  }
}
